import { useState } from "react";
import { QUIZ_QUESTIONS } from "@/data/quizQuestions";
import { Check, X, RotateCcw, Trophy, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import { SFX, unlockAudio } from "@/lib/audio";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ShareCard from "@/components/site/ShareCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [name, setName] = useState(
    () => localStorage.getItem("tiger_player_name") || "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const q = QUIZ_QUESTIONS[step];

  const choose = (i) => {
    if (chosen !== null) return;
    unlockAudio();
    setChosen(i);
    if (i === q.answerIndex) {
      setScore((s) => s + 1);
      SFX.star();
    } else {
      SFX.hit();
    }
  };

  const next = () => {
    if (step + 1 >= QUIZ_QUESTIONS.length) {
      setDone(true);
      SFX.win();
    } else {
      setStep((s) => s + 1);
      setChosen(null);
    }
  };

  const restart = () => {
    setStep(0);
    setChosen(null);
    setScore(0);
    setDone(false);
    setSubmitted(false);
  };

  const saveScore = async () => {
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error("Please type your name first!");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quiz/score`, {
        name: cleanName,
        score,
        total: QUIZ_QUESTIONS.length,
      });
      localStorage.setItem("tiger_player_name", cleanName);
      setSubmitted(true);
      SFX.star();
      toast.success("Score saved to the leaderboard! 🏆");
    } catch (err) {
      console.error("Failed to save quiz score", err);
      toast.error("Could not save your score. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const total = QUIZ_QUESTIONS.length;
    const message =
      score === total
        ? "PERFECT! 🌟🌟🌟 You're a true Tiger Expert!"
        : score >= 3
          ? "Awesome job! 🎉 You really know your tigers!"
          : "Good try! Read the facts again and have another go.";
    return (
      <div
        className="rounded-3xl border-[4px] border-tiger-deep bg-tiger-cream p-6 sm:p-10 shadow-stickerLg animate-pop-in"
        data-testid="quiz-result"
      >
        <div className="flex flex-col items-center text-center">
          <div className="grid h-20 w-20 place-items-center rounded-3xl border-[4px] border-tiger-deep bg-tiger-orange shadow-stickerLg">
            <Trophy className="h-10 w-10 text-white" />
          </div>
          <h3 className="mt-4 font-heading text-3xl font-bold text-tiger-deep sm:text-4xl">
            You scored {score}/{total}!
          </h3>
          <p className="mt-2 max-w-md font-body text-lg text-tiger-deep">
            {message}
          </p>

          {!submitted && (
            <div className="mt-6 w-full max-w-md rounded-2xl border-[3px] border-tiger-deep bg-white p-4 shadow-sticker">
              <p className="font-heading text-base font-bold text-tiger-deep">
                Add your name to the leaderboard!
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  maxLength={20}
                  className="flex-1 rounded-2xl border-[3px] border-tiger-deep bg-tiger-cream px-4 py-2 font-body text-base font-bold text-tiger-deep placeholder:text-tiger-deep/40 focus:outline-none focus:bg-white"
                  data-testid="quiz-name-input"
                />
                <button
                  onClick={saveScore}
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 rounded-full border-[3px] border-tiger-deep bg-jungle-leaf px-5 py-2 font-heading text-base font-bold text-white shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform disabled:opacity-60"
                  data-testid="quiz-save-score"
                >
                  <Save className="h-4 w-4" />
                  {submitting ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          )}

          {submitted && (
            <p
              className="mt-4 inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-jungle-leaf px-4 py-1.5 font-heading text-sm font-bold text-white shadow-sticker"
              data-testid="quiz-saved-badge"
            >
              <Check className="h-4 w-4" /> Saved to leaderboard!
            </p>
          )}

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              onClick={restart}
              className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-white px-6 py-3 font-heading text-lg font-bold text-tiger-deep shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform"
              data-testid="quiz-restart"
            >
              <RotateCcw className="h-5 w-5" /> Play Again
            </button>
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-sun px-6 py-3 font-heading text-lg font-bold text-tiger-deep shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform"
              data-testid="quiz-leaderboard-link"
            >
              🏆 Leaderboard
            </Link>
            <Link
              to="/game"
              className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-6 py-3 font-heading text-lg font-bold text-white shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform"
              data-testid="quiz-play-game"
            >
              🎮 Play the Tiger Game!
            </Link>
          </div>

          <div className="mt-8 w-full text-left">
            <ShareCard
              title="Brag a little! 📣"
              subtitle="Send your score to a friend and challenge them to beat you."
              playerName={name.trim() || undefined}
              quizScore={{ score, total }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-3xl border-[4px] border-tiger-deep bg-white p-6 sm:p-8 shadow-stickerLg"
      data-testid="quiz-container"
    >
      <div className="flex items-center justify-between gap-4">
        <p className="font-heading text-sm font-bold uppercase tracking-widest text-tiger-orange">
          Quiz Time! 🎯
        </p>
        <p
          className="font-body text-sm font-bold text-tiger-deep/70"
          data-testid="quiz-progress"
        >
          {step + 1} / {QUIZ_QUESTIONS.length}
        </p>
      </div>

      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-tiger-cream border-[2px] border-tiger-deep">
        <div
          className="h-full bg-tiger-orange transition-all duration-300"
          style={{ width: `${((step + 1) / QUIZ_QUESTIONS.length) * 100}%` }}
        />
      </div>

      <h3 className="mt-5 font-heading text-2xl font-bold leading-tight text-tiger-deep sm:text-3xl">
        {q.question}
      </h3>

      <div
        className="mt-6 grid gap-3 sm:grid-cols-2"
        data-testid="quiz-options"
      >
        {q.options.map((opt, i) => {
          const isChosen = chosen === i;
          const isCorrect = i === q.answerIndex;
          const showState = chosen !== null;
          return (
            <button
              key={i}
              onClick={() => choose(i)}
              disabled={chosen !== null}
              className={cn(
                "group flex items-center gap-3 rounded-2xl border-[3px] border-tiger-deep bg-tiger-cream px-4 py-4 text-left font-body text-lg font-bold text-tiger-deep shadow-sticker transition-all",
                !showState && "hover:-translate-y-0.5 hover:bg-sun",
                showState && isCorrect && "bg-jungle-leaf text-white",
                showState && isChosen && !isCorrect && "bg-red-400 text-white",
                showState && !isChosen && !isCorrect && "opacity-60",
              )}
              data-testid={`quiz-option-${i}`}
            >
              <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl border-[3px] border-tiger-deep bg-white font-heading text-tiger-deep">
                {String.fromCharCode(65 + i)}
              </span>
              <span className="flex-1">{opt}</span>
              {showState && isCorrect && <Check className="h-6 w-6" />}
              {showState && isChosen && !isCorrect && <X className="h-6 w-6" />}
            </button>
          );
        })}
      </div>

      {chosen !== null && (
        <div
          className="mt-5 flex flex-col gap-4 rounded-2xl border-[3px] border-tiger-deep bg-sun px-4 py-3 sm:flex-row sm:items-center sm:justify-between shadow-sticker animate-fade-up"
          data-testid="quiz-feedback"
        >
          <p className="font-body text-base font-bold text-tiger-deep sm:text-lg">
            {chosen === q.answerIndex ? "Correct! 🎉" : "Not quite!"} {q.fact}
          </p>
          <button
            onClick={next}
            className="inline-flex items-center justify-center gap-2 rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-5 py-2 font-heading text-base font-bold text-white shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform whitespace-nowrap"
            data-testid="quiz-next"
          >
            {step + 1 >= QUIZ_QUESTIONS.length ? "See Result" : "Next →"}
          </button>
        </div>
      )}
    </div>
  );
}
